import BaseApi from "src/store/BaseApi";
import {
  Brand,
  CategoryRequest,
  CategoryResponse,
  getItemRequest,
  getItemsRequest,
  ItemByModule,
  ItemDetails,
  ItemDTO,
  ModuleRequest,
  ModuleType,
  OrderItemRequest,
  OrderItemResponse,
  RCItemResponse
} from "./item.type";
import ItemTransformer from "./item.transformer";
import { Option, RCResponse } from "../common/common.type";

const PrivateAPIWithTags = BaseApi.enhanceEndpoints({
  addTagTypes: ["userDetails"],
});

export const ItemsApi = PrivateAPIWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getCategories: builder.query<Option[], ModuleRequest<CategoryRequest>>({
      keepUnusedDataFor: 600,
      query: ({ moduleType, ...body }) => {
        return {
          url: `v1.0/Category`,
          method: "POST",
          body,
        };
      },
      transformResponse: (response: RCResponse<CategoryResponse>) => {
        return response.result.data.filter((category) => category.name).map((category) => ({ value: category.code, label: category.name }));
      },
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getItemDetails: builder.query<ItemDetails, ModuleRequest<getItemRequest>>({
      keepUnusedDataFor: 600,
      query: ({ moduleType, ...body }) => {
        return {
          url: `v1.0/item/Details`,
          method: "POST",
          body,
        };
      },
      transformResponse: (response: RCResponse<RCItemResponse<ItemDTO[]>>, _meta, { moduleType }) => {
        const [first, ...rest] = response.result.data;
        if (!first) throw new Error("Item details returned no items");
        return ItemTransformer.toDetails(moduleType, [first, ...rest]);
      },
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getItemsWithDetails: builder.query<Brand[], ModuleRequest<getItemsRequest>>({
      keepUnusedDataFor: 600,
      query: ({ moduleType, ...body }) => ({
        url: `v1.0/item/WithDetails`,
        method: "POST",
        body,
      }),
      transformResponse: (response: RCResponse<RCItemResponse<(Omit<Brand, "items"> & { items: ItemDTO[] })[]>>, _meta, { moduleType }) =>
        response.result.data.map((brand) => ({ ...brand, items: brand.items.map((item) => ItemTransformer.toItem(moduleType, item)) })),
      extraOptions: { notifyOnFailure: true },
    }),
    orderItem: builder.mutation<OrderItemResponse, OrderItemRequest>({
      query: (body) => ({
        url: `Order/Checkout`,
        method: "POST",
        body,
      }),
      transformResponse: (response: RCResponse<OrderItemResponse>) => {
        return response?.result;
      },
      invalidatesTags: ["userDetails"],
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
  }),
});

type ItemsApiEndpoints = keyof typeof ItemsApi.endpoints;

type ModuleQueryResult<R, D> = Omit<R, "data"> & { data?: D;};

const useGetItemDetailsQuery = <M extends ModuleType>(
  args: ModuleRequest<getItemRequest, M>,
  options?: Parameters<typeof ItemsApi.useGetItemDetailsQuery>[1],
) =>
  ItemsApi.useGetItemDetailsQuery(args, options) as ModuleQueryResult<
    ReturnType<typeof ItemsApi.useGetItemDetailsQuery>,
    ItemDetails<ItemByModule[M]>
  >;

const useGetItemsWithDetailsQuery = <M extends ModuleType>(
  args: ModuleRequest<getItemsRequest, M>,
  options?: Parameters<typeof ItemsApi.useGetItemsWithDetailsQuery>[1],
) =>
  ItemsApi.useGetItemsWithDetailsQuery(args, options) as ModuleQueryResult<
    ReturnType<typeof ItemsApi.useGetItemsWithDetailsQuery>,
    Brand<ItemByModule[M]>[]
  >;

export { type ItemsApiEndpoints, useGetItemDetailsQuery, useGetItemsWithDetailsQuery };
export const { useGetCategoriesQuery, useOrderItemMutation } = ItemsApi;
